import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '../../lib/utils';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  ChevronUp,
  X,
} from 'lucide-react';

interface MantraPlayerProps {
  currentVerse?: {
    mandala: number;
    hymn: number;
    verse: number;
    sanskrit?: string;
    transliteration?: string;
    translation?: string;
  };
  isPlaying?: boolean;
  onPlayPause?: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onOpenFull?: () => void;
  className?: string;
}

export const MantraPlayer: React.FC<MantraPlayerProps> = ({
  currentVerse,
  isPlaying = false,
  onPlayPause,
  onNext,
  onPrevious,
  onOpenFull,
  className,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);

  const audioSrc = currentVerse
    ? `/api/audio/${currentVerse.mandala}/${currentVerse.hymn}`
    : '';

  // Create / swap audio element when hymn changes
  useEffect(() => {
    if (!audioSrc) return;

    const audio = new Audio(audioSrc);
    audio.preload = 'metadata';
    audioRef.current = audio;
    setAudioLoaded(false);
    setAudioError(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);

    const onLoaded = () => {
      setDuration(audio.duration);
      setAudioLoaded(true);
    };
    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration > 0) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };
    const onEnded = () => {
      onPlayPause?.();
      setProgress(0);
      setCurrentTime(0);
    };
    const onError = () => setAudioError(true);

    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    return () => {
      audio.pause();
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.src = '';
    };
  }, [audioSrc, onPlayPause]);

  // Sync play/pause with prop
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioLoaded) return;

    if (isPlaying) {
      audio.play().catch(() => setAudioError(true));
    } else {
      audio.pause();
    }
  }, [isPlaying, audioLoaded]);

  // Sync mute
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const formatTime = useCallback((seconds: number) => {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }, []);

  // Seek on progress bar click
  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      const bar = progressBarRef.current;
      if (!audio || !bar || !duration) return;

      const rect = bar.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      audio.currentTime = ratio * duration;
    },
    [duration],
  );

  if (!currentVerse) return null;

  return (
    <div
      className={cn(
        'fixed bottom-16 left-1/2 -translate-x-1/2 z-30',
        'w-[calc(100%-3rem)] max-w-2xl',
        className,
      )}
    >
      <div
        className={cn(
          'bg-white/95 dark:bg-museum-950/95',
          'backdrop-blur-md',
          'border border-museum-200 dark:border-museum-800',
          'rounded-museum-lg',
          'shadow-museum-elevated',
          'transition-all duration-300 ease-museum-smooth',
        )}
      >
        {/* Progress bar — thin line at top */}
        <div
          ref={progressBarRef}
          className="relative h-1 cursor-pointer group"
          onClick={handleSeek}
        >
          <div className="absolute inset-0 bg-museum-100 dark:bg-museum-800 rounded-t-museum-lg" />
          <div
            className="absolute inset-y-0 left-0 bg-museum-900 dark:bg-museum-200 rounded-t-museum-lg transition-[width] duration-100"
            style={{ width: `${progress}%` }}
          />
          {/* Hover scrub indicator */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <div
              className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-museum-900 dark:bg-museum-100 shadow-sm"
              style={{ left: `calc(${progress}% - 5px)` }}
            />
          </div>
        </div>

        {/* Main controls row */}
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Track info */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={cn(
              'flex items-center justify-center w-9 h-9 shrink-0',
              'border border-museum-200 dark:border-museum-700',
              'rounded-museum',
            )}>
              <span className="font-display text-lg font-light text-museum-400 dark:text-museum-500">
                {currentVerse.mandala}
              </span>
            </div>

            <div className="flex flex-col min-w-0">
              <span className="font-display text-sm text-museum-800 dark:text-museum-200 truncate">
                Mandala {currentVerse.mandala}, Hymn {currentVerse.hymn}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-museum-400 dark:text-museum-500">
                Verse {currentVerse.verse}
                {duration > 0 && (
                  <> &middot; {formatTime(currentTime)} / {formatTime(duration)}</>
                )}
              </span>
            </div>
          </div>

          {/* Playback controls */}
          <div className="flex items-center gap-1">
            <button
              className="p-2 text-museum-400 hover:text-museum-700 dark:text-museum-500 dark:hover:text-museum-200 transition-colors"
              onClick={onPrevious}
              aria-label="Previous verse"
            >
              <SkipBack className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>

            <button
              className={cn(
                'flex items-center justify-center w-10 h-10',
                'rounded-full',
                'bg-museum-900 dark:bg-museum-100',
                'text-white dark:text-museum-900',
                'hover:bg-museum-800 dark:hover:bg-museum-200',
                'transition-colors duration-200',
                audioError && 'opacity-40 cursor-not-allowed',
              )}
              onClick={onPlayPause}
              disabled={audioError}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <Pause className="h-4 w-4" strokeWidth={2} />
              ) : (
                <Play className="h-4 w-4 ml-0.5" strokeWidth={2} />
              )}
            </button>

            <button
              className="p-2 text-museum-400 hover:text-museum-700 dark:text-museum-500 dark:hover:text-museum-200 transition-colors"
              onClick={onNext}
              aria-label="Next verse"
            >
              <SkipForward className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>

          {/* Right controls */}
          <div className="flex items-center gap-1">
            <button
              className="p-2 text-museum-400 hover:text-museum-700 dark:text-museum-500 dark:hover:text-museum-200 transition-colors"
              onClick={() => setIsMuted((v) => !v)}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <VolumeX className="h-3.5 w-3.5" strokeWidth={1.5} />
              ) : (
                <Volume2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              )}
            </button>

            <button
              className="p-2 text-museum-400 hover:text-museum-700 dark:text-museum-500 dark:hover:text-museum-200 transition-colors"
              onClick={() => setIsExpanded((v) => !v)}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? (
                <X className="h-3.5 w-3.5" strokeWidth={1.5} />
              ) : (
                <ChevronUp className="h-3.5 w-3.5" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>

        {/* Expanded content — sanskrit preview */}
        {isExpanded && currentVerse.sanskrit && (
          <div className="px-4 pb-4 border-t border-museum-100 dark:border-museum-800">
            <p className="font-sanskrit text-base text-center leading-relaxed pt-3 text-museum-700 dark:text-museum-300">
              {currentVerse.sanskrit}
            </p>
            {currentVerse.translation && (
              <p className="font-body italic text-xs text-center mt-2 text-museum-400 dark:text-museum-500 line-clamp-2">
                {currentVerse.translation}
              </p>
            )}
          </div>
        )}

        {/* Audio error state */}
        {audioError && (
          <div className="px-4 pb-2">
            <p className="font-mono text-[10px] text-center text-museum-400 dark:text-museum-500">
              Audio not available for this hymn
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MantraPlayer;
